import { Dispatch } from '@reduxjs/toolkit';

import { Types, Actions } from './actions';
import authApi from '~/api';
import { CONFIRM_EMAIL_ENDPOINT, SIGNUP_ENDPOINT } from '~/api/constants';
import {
  ACCOUNT_ACTIVATED,
  ACCOUNT_CREATED,
  ACCOUNT_CREATION_FAILED,
} from '~/components/analytics/constants';
import identify from '~/components/analytics/identify';
import track from '~/components/analytics/track';
import { getProductId } from '~/components/context';
import { Actions as AuthActions } from '~/store/auth/actions';
import { getAllQueryParams } from '~/utils/browser';
import { get } from '~/utils/object';

const scrubRegistrationPayload = (payload: any) => {
  // we destructure password so that it isn't passed to the track call
  // but we don't have an actual use for it.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { password, ...properties } = payload;
  return properties;
};

const logRegistrationSuccess = (payload: any) => {
  const params = getAllQueryParams();
  const urlParameters = {} as { [key: string]: string };
  params.forEach((value: string, key: string) => {
    urlParameters[key] = value;
  });

  const properties = {
    ...scrubRegistrationPayload(payload),
    product: getProductId(),
    urlParameters,
  };
  identify(
    {
      ...properties,
      key: properties.userId,
    },
    { ...urlParameters },
  );
  setTimeout(() => {
    track(ACCOUNT_CREATED, properties);
  }, 2000);
};

const logRegistrationFailure = (payload: any) => {
  const properties = scrubRegistrationPayload(payload);
  track(ACCOUNT_CREATION_FAILED, properties);
};

const postRegistration = (dispatch: Dispatch, payload: any) => {
  const {
    name,
    email,
    username,
    password,
    company = null,
    jobTitle = null,
  } = payload;

  authApi
    .post(SIGNUP_ENDPOINT, {
      email: username ? username : email,
      password,
      name,
      organization_name: company,
      profile: { jobTitle },
    })
    .then(res => {
      const { ok, data } = res;
      if (ok) {
        const user = get(data, 'user', { name, company, email });
        const token = get<string>(data, 'accessToken', '');
        if (user) {
          dispatch(AuthActions.SET_USER({ user }));
        }
        if (token !== '') {
          dispatch(AuthActions.SET_TOKEN({ token }));
        }
        const userId = get(data, 'key');
        const successPayload = { ...payload, userId };
        dispatch(Actions.REGISTRATION_SUCCESS(successPayload));
        logRegistrationSuccess(successPayload);
      } else {
        const message = get<string>(
          data,
          'error.message',
          'Failed to create account',
        );
        dispatch(Actions.REGISTRATION_FAILURE(message));
        logRegistrationFailure({ data, payload });
      }
    })
    .catch(err => {
      dispatch(Actions.REGISTRATION_FAILURE(err));
    });
};

const postConfirmEmail = (dispatch: Dispatch, payload: any) => {
  const { resetToken } = payload;
  authApi
    .post(CONFIRM_EMAIL_ENDPOINT, { resetToken })
    .then(res => {
      const { ok, status, data } = res;
      if (ok) {
        dispatch(Actions.CONFIRM_EMAIL_SUCCESS());
        logEmailConfirmation(data);
      } else if (status === 401) {
        //when the reset token has expired
        dispatch(Actions.CONFIRM_EMAIL_ERROR());
      } else {
        dispatch(Actions.CONFIRM_EMAIL_FAILURE());
      }
    })
    .catch(err => {
      dispatch(Actions.CONFIRM_EMAIL_FAILURE(err));
    });
};

const logEmailConfirmation = (data: any) => {
  const properties = {
    ...data,
    product: getProductId(),
  };
  track(ACCOUNT_ACTIVATED, properties);
};

const middleware = (store: any) => (next: any) => (action: any) => {
  const { dispatch } = store;
  const payload = get(action, 'payload', {});
  if (action.type === Types.REGISTRATION_REQUEST) {
    postRegistration(dispatch, payload);
  }
  if (action.type === Types.CONFIRM_EMAIL_REQUEST) {
    postConfirmEmail(dispatch, payload);
  }
  next(action);
};

export default middleware;
